/**
 * PaytableOverlay.jsx — Paytable overlay shell
 *
 * Thin wrapper: backdrop, glow decoration, header/title/separator,
 * close button, data extraction, slide assembly.
 *
 * Carousel engine lives in PaytableSlider.jsx.
 * All config constants live in paytableConfig.js.
 */

import React, {
  useEffect,
  useLayoutEffect,
  useState,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";

import {
  useDimensions,
  ASPECT_RATIO_BREAKPOINTS,
} from "../../../../Contexts/DimensionsContext";
import { closePaytable, closeMenu } from "../../../../State/actions";
import { selectPaytableActive } from "../../../../State/selectors";
import {
  selectPaytable,
  selectBet,
  selectBaseBet,
  selectCurrency,
} from "../../../../State/Slices/gameSettingsSlice";

import { PixiSettings } from "../../../../pixiSettings";
import { useSecondaryToolbar } from "../../../../Hooks/useSecondaryToolbar";
import { useIosOverlayGuard } from "../../../../Hooks/useIosOverlayGuard";
import { useOverlayFade } from "../../../../Hooks/useOverlayFade";
import { getUITextureAsDataURL, getGameImageSrc } from "../../../../assets";

import { soundManager } from "../../../../SoundManager";
import { SOUND_NAME } from "../../../../sounds";

import SpineTextureExtractor from "../../../../utils/SpineTextureExtractor";

import PaytableSlider from "./PaytableSlider";

import {
  BASE_FONT_SIZE,
  CHROME_DEVICE_CONFIG,
  CHROME_RATIOS,
  DESC_FONT_CONFIG,
  getGlobalDescFontMultiplier,
  SLIDE_TITLES,
  SLIDE_CONTENT_SPACED_GAP_MUL,
  STANDARD_MOBILE_LANDSCAPE_TYPO,
  SMALL_MOBILE_PORTRAIT_TYPO,
  FEATURE_SLIDES,
  WILD_SLIDE,
  SYMBOL_SLIDE_1_CONFIG,
  SYMBOL_SLIDE_1,
  SYMBOL_SLIDE_2_CONFIG,
  SYMBOL_SLIDE_2_HEADER,
  SYMBOL_SLIDE_2,
  SYMBOL_SLIDE_2_IMG_RATIO,
  SYMBOL_SLIDE_4_CONFIG,
  getDeviceConfig,
  getImgWidth,
  getZeusSlideLayoutVal,
  getMeasureCanvas,
  measureTextHeight,
  getSymbolGroups,
  getPayValues,
  getPaytableArrowLayout,
  PAYTABLE_TUNED_LANDSCAPE_CHROME,
  isBrowserChromeShortenedPortrait,
  getPaytableTunedLandscapeSlideAreaPad,
  getPaytableTunedLandscapeSideOffset,
  getPaytableLayoutDevice,
} from "./paytableConfig";

// Feature-slide description fit-to-height safety margin — target this fraction
// of the available height so descenders aren't clipped by the container.
const FEATURE_DESC_FILL_SAFETY = 0.98;
// Portrait stacked layout — canvas measure runs slightly tall vs DOM, so allow
// a touch more fill before stepping down (reclaim pass still adds up to 1px).
const FEATURE_DESC_FILL_SAFETY_PORTRAIT = 0.99;

// ── Component ──

const PaytableOverlay = () => {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const paytableActive = useSelector(selectPaytableActive);
  const paytable = useSelector(selectPaytable);
  const bet = useSelector(selectBet);
  const baseBet = useSelector(selectBaseBet);
  const currency = useSelector(selectCurrency);

  const {
    dimensions,
    device,
    orientation,
    uiSymbolScale,
    uiSymbolSize,
    isMobileSmall,
  } = useDimensions();
  const toolbar = useSecondaryToolbar();
  // Tablet uses MOBILE layout keys — only uiSymbolScale/viewport size differs.
  // Desktop adopts the standard-mobile-landscape baseline (mirrored via
  // isMirroredMobileLandscape below), so it also reads MOBILE layout keys.
  const isDesktop = device === PixiSettings.DEVICE.DESKTOP;
  // Wide desktop is a refinement of desktop: it inherits every desktop layout
  // branch and only overrides the values gated on isWideDesktop below. Derived
  // straight from dimensions (like useSecondaryToolbar) — the context collapses
  // all desktop widths to DEVICE.DESKTOP, so we read the width tier here.
  // Covers wide + ultra-wide (both ≥ WIDE_DESKTOP_MIN_WIDTH, 2540px).
  const isWideDesktop =
    isDesktop &&
    dimensions.width >= ASPECT_RATIO_BREAKPOINTS.WIDE_DESKTOP_MIN_WIDTH;
  const layoutDevice = isDesktop
    ? PixiSettings.DEVICE.MOBILE
    : getPaytableLayoutDevice(device);

  const { isHidden } = useIosOverlayGuard();

  // Mount + fade lifecycle — shared hook (animejs on the RAF ticker, no timers).
  const { shouldRender, overlayRef } = useOverlayFade({
    active: paytableActive,
    isHidden,
  });

  // Re-measure trigger. The fit effects below read container sizes from the
  // DOM, but on a drag-resize the containers lag the viewport by a frame, so a
  // synchronous measure runs against an unsettled size and never corrects
  // (fine on reload — a single settled measure). Bumping this on the frame
  // after any dimensions change forces one more measure pass once layout has
  // settled; the state-change guards make it a no-op when nothing moved.
  const [measureTick, setMeasureTick] = useState(0);
  useEffect(() => {
    if (!shouldRender) return undefined;
    const raf = requestAnimationFrame(() => setMeasureTick((t) => t + 1));
    return () => cancelAnimationFrame(raf);
  }, [shouldRender, dimensions.width, dimensions.height]);

  // ── Responsive sizing — Single chrome computation for Overlay + Slider ──
  const isPortrait =
    (device === PixiSettings.DEVICE.MOBILE ||
      device === PixiSettings.DEVICE.TABLET) &&
    orientation === PixiSettings.ORIENTATION.PORTRAIT;

  const chrome = useMemo(() => {
    const config = getDeviceConfig(
      CHROME_DEVICE_CONFIG,
      layoutDevice,
      orientation,
    );
    const base = BASE_FONT_SIZE * config.fontMultiplier * uiSymbolScale;
    return {
      title: Math.round(base * CHROME_RATIOS.title),
      subtitle: Math.round(base * CHROME_RATIOS.subtitle),
      arrow: Math.round(
        base * CHROME_RATIOS.arrow * (config.arrowScale || 1.0),
      ),
      close: Math.round(base * CHROME_RATIOS.close),
      dot: Math.round(base * CHROME_RATIOS.dot),
      separator: Math.round(base * CHROME_RATIOS.separatorHeight),
      topPad: Math.round(base * CHROME_RATIOS.topPad),
      base,
    };
  }, [layoutDevice, orientation, uiSymbolScale]);

  // ── Shell layout — standard mobile portrait aligned with GameRulesOverlay ──
  const isTablet = device === PixiSettings.DEVICE.TABLET;
  const isStandardMobile =
    device === PixiSettings.DEVICE.MOBILE && !isMobileSmall;
  // Toolbar-shortened viewport → small-mobile portrait shell. Fullscreen tall
  // phones (game height ≈ screen height) keep standard-mobile padding even when
  // visible CSS height is under SHORT_PORTRAIT_HEIGHT. See
  // isBrowserChromeShortenedPortrait in paytableConfig.js.
  const isShortStandardPortrait =
    isStandardMobile &&
    isPortrait &&
    isBrowserChromeShortenedPortrait(dimensions.height);
  const isStandardMobilePortrait =
    isStandardMobile && isPortrait && !isShortStandardPortrait;
  const isStandardMobileLandscape = isStandardMobile && !isPortrait;
  const isTabletPortrait = isTablet && isPortrait;
  const isTabletLandscape = isTablet && !isPortrait;
  const isSmallMobilePortrait =
    isPortrait && (isMobileSmall || isShortStandardPortrait);
  const isSmallMobileLandscape = isMobileSmall && !isPortrait;
  const isTunedStandardPortrait = isStandardMobilePortrait || isTabletPortrait;
  // Tablet landscape and desktop both mirror standard mobile landscape exactly
  // (0 layout diffs); desktop then tunes via desktop* config knobs.
  const isMirroredMobileLandscape =
    isStandardMobileLandscape || isTabletLandscape || isDesktop;
  const isTunedMobileLandscape =
    isMirroredMobileLandscape || isSmallMobileLandscape;
  const isTunedShellPortrait = isTunedStandardPortrait || isSmallMobilePortrait;

  const shellTopPad = isStandardMobilePortrait
    ? chrome.topPad * 7.5
    : isTabletPortrait
      ? chrome.topPad * 0.8
      : isTabletLandscape
        ? chrome.topPad * 1.4 * 6.5
        : isWideDesktop
          ? chrome.topPad *
            PAYTABLE_TUNED_LANDSCAPE_CHROME.wideDesktopShellTopPadMul
          : isDesktop
            ? chrome.topPad * 1.4
            : isTunedMobileLandscape
            ? chrome.topPad * 1.6
            : isSmallMobilePortrait
              ? chrome.topPad * 0.8
              : Math.round(chrome.topPad * 1.3);

  const shellBottomPad = isStandardMobilePortrait
    ? chrome.topPad * 7.75
    : isTabletPortrait
      ? chrome.topPad * 1.5
      : isTabletLandscape
        ? chrome.topPad * 3.3 * 3.35
        : isWideDesktop
          ? chrome.topPad *
            PAYTABLE_TUNED_LANDSCAPE_CHROME.wideDesktopShellBottomPadMul
          : isTunedMobileLandscape
          ? chrome.topPad * 3.3
          : isSmallMobilePortrait
            ? chrome.topPad * 1.5
            : isPortrait
              ? chrome.topPad * 3.8
              : chrome.topPad * 3.2;

  // Content width is a fixed multiple of the (viewport-fit) symbol size, so it
  // scales continuously with the layout — no absolute cap. uiSymbolSize is
  // already fit to the viewport by DimensionsContext (its scale is the min of
  // width-fit / height-fit / scaleCap), so a multiple of it can never overflow;
  // when the window shrinks the symbols shrink and the popup shrinks with them.
  const contentWidthSymbolMul = isWideDesktop
    ? PAYTABLE_TUNED_LANDSCAPE_CHROME.wideDesktopContentWidthSymbolMul
    : isDesktop
    ? PAYTABLE_TUNED_LANDSCAPE_CHROME.desktopContentWidthSymbolMul
    : isSmallMobileLandscape
      ? PAYTABLE_TUNED_LANDSCAPE_CHROME.smallMobileLandscapeContentWidthSymbolMul
      : isTabletLandscape
        ? PAYTABLE_TUNED_LANDSCAPE_CHROME.tabletLandscapeContentWidthSymbolMul
        : PAYTABLE_TUNED_LANDSCAPE_CHROME.standardMobileLandscapeContentWidthSymbolMul;
  const tunedMobileLandscapeShellWidth = isTunedMobileLandscape
    ? Math.round(uiSymbolSize * contentWidthSymbolMul)
    : null;

  const tabletPortraitContentWidth = isTabletPortrait
    ? dimensions.width * 0.5
    : null;

  const shellContentWidth = isStandardMobilePortrait
    ? dimensions.width * 0.75
    : isTabletPortrait
      ? tabletPortraitContentWidth
      : isSmallMobilePortrait
        ? dimensions.width * 0.8
        : (tunedMobileLandscapeShellWidth ?? undefined);

  const separatorLineWidth = isStandardMobilePortrait
    ? Math.min(chrome.title * 12, dimensions.width * 0.95)
    : isTabletPortrait
      ? tabletPortraitContentWidth
      : isTunedMobileLandscape
        ? tunedMobileLandscapeShellWidth
        : isPortrait
          ? Math.min(chrome.title * 12, dimensions.width * 0.92)
          : Math.min(chrome.title * 16, dimensions.width * 0.65);

  const separatorHeight = isTunedStandardPortrait
    ? Math.round(chrome.separator * 1.25)
    : isWideDesktop
      ? Math.round(
          chrome.separator *
            PAYTABLE_TUNED_LANDSCAPE_CHROME.wideDesktopSeparatorMul,
        )
      : isDesktop
      ? Math.round(chrome.separator * 1.0)
      : isTunedMobileLandscape
        ? Math.round(chrome.separator * 1.2)
        : chrome.separator;

  const closeButtonSize = isTunedMobileLandscape
    ? Math.round(chrome.close * PAYTABLE_TUNED_LANDSCAPE_CHROME.closeMul)
    : chrome.close;

  const tunedLandscapeSlideAreaPad =
    isTunedMobileLandscape && shellContentWidth != null
      ? getPaytableTunedLandscapeSlideAreaPad(
          dimensions.width,
          shellContentWidth,
        )
      : null;
  const tunedLandscapeChromeSideOffset =
    tunedLandscapeSlideAreaPad != null
      ? getPaytableTunedLandscapeSideOffset(chrome, tunedLandscapeSlideAreaPad)
      : null;

  const closeButtonRight = isPortrait
    ? Math.max(6, Math.round(chrome.close * 0.12))
    : tunedLandscapeChromeSideOffset != null
      ? Math.max(4, tunedLandscapeChromeSideOffset - Math.round(chrome.close * 0.35))
      : shellContentWidth != null
        ? Math.max(
            4,
            Math.round((dimensions.width - shellContentWidth) / 2) -
              Math.round(chrome.close * 0.8),
          )
        : Math.round(
            (dimensions.width - separatorLineWidth) / 2 - chrome.close * 3.0,
          );

  const linePad = Math.round(chrome.title * 0.5);
  const slideContentGap = isTunedMobileLandscape
    ? Math.round(linePad * 1.5)
    : linePad;
  const slideContentSpacedGapMul = isTunedStandardPortrait
    ? SLIDE_CONTENT_SPACED_GAP_MUL.mobilePortrait
    : isSmallMobilePortrait
      ? SLIDE_CONTENT_SPACED_GAP_MUL.mobileSmallPortrait
      : isTunedMobileLandscape
        ? SLIDE_CONTENT_SPACED_GAP_MUL.mobileLandscape
        : SLIDE_CONTENT_SPACED_GAP_MUL.default;
  const slideContentSpacedGap = isWideDesktop
    ? Math.round(
        linePad * PAYTABLE_TUNED_LANDSCAPE_CHROME.wideDesktopSlideContentSpacedGapMul,
      )
    : isDesktop
    ? Math.round(linePad * 1.0)
    : isTunedMobileLandscape
      ? slideContentGap
      : Math.round(linePad * slideContentSpacedGapMul);
  const separatorMarginTop = isTunedStandardPortrait
    ? Math.round(linePad * 0.6)
    : isSmallMobilePortrait
      ? Math.round(linePad * 0.6)
      : 0;
  const separatorMarginBottom = isTunedStandardPortrait
    ? Math.round(linePad * 0.6)
    : isSmallMobilePortrait
      ? Math.round(linePad * 0.6)
      : isTunedMobileLandscape
        ? Math.round(linePad * 0.6)
        : 0;
  const footerMarginTop = isTunedMobileLandscape
    ? Math.round(linePad * 1.0)
    : separatorMarginBottom;
  const headerPaddingBottom = isTunedShellPortrait
    ? 0
    : Math.round(linePad * 0.25);

  const landscapeUniformTypo = isTunedMobileLandscape
    ? STANDARD_MOBILE_LANDSCAPE_TYPO
    : null;
  const landscapeFixedDescFont = landscapeUniformTypo?.fixedDescFont ?? false;
  const uniformDescFontMul = landscapeUniformTypo?.descFontMul ?? null;
  const uniformFeatureSubtitleMul =
    landscapeUniformTypo?.featureSubtitleMul ?? null;

  // Global description font multiplier — one number for the current device/
  // orientation tier + locale, layered on top of every slide's own descFontMul.
  // See getGlobalDescFontMultiplier in paytableConfig.js for the full rationale.
  const descFontTierKey = isSmallMobilePortrait
    ? "mobileSmallPortrait"
    : isSmallMobileLandscape
      ? "mobileSmallLandscape"
      : isStandardMobilePortrait
        ? "mobilePortrait"
        : isStandardMobileLandscape
          ? "mobileLandscape"
          : isTabletPortrait
            ? "tabletPortrait"
            : isTabletLandscape
              ? "tabletLandscape"
              : isWideDesktop
                ? "wideDesktop"
                : "desktop";
  const globalDescFontMultiplier = useMemo(
    () => getGlobalDescFontMultiplier(descFontTierKey, i18n.language),
    [descFontTierKey, i18n.language],
  );

  const getSlideDescFontMul = useCallback(
    (defaultMul = 0.8) =>
      (uniformDescFontMul ?? defaultMul) * globalDescFontMultiplier,
    [uniformDescFontMul, globalDescFontMultiplier],
  );
  const smallMobilePortraitSubtitleMul = isSmallMobilePortrait
    ? SMALL_MOBILE_PORTRAIT_TYPO.subtitleFontMul
    : 1;
  const getUniformFeatureSubtitleH = useCallback(
    (measureMul = 1) =>
      uniformFeatureSubtitleMul != null
        ? Math.round(
            chrome.subtitle * 0.7 * uniformFeatureSubtitleMul * measureMul,
          )
        : Math.round(
            chrome.subtitle * 0.7 * smallMobilePortraitSubtitleMul * measureMul,
          ),
    [
      uniformFeatureSubtitleMul,
      chrome.subtitle,
      smallMobilePortraitSubtitleMul,
    ],
  );

  const headerTitleFontSize = isTunedStandardPortrait
    ? Math.round(chrome.title * 0.94)
    : isWideDesktop
      ? Math.round(
          chrome.title *
            PAYTABLE_TUNED_LANDSCAPE_CHROME.wideDesktopHeaderTitleMul,
        )
      : isDesktop
      ? Math.round(
          chrome.title * PAYTABLE_TUNED_LANDSCAPE_CHROME.desktopHeaderTitleMul,
        )
      : isTunedMobileLandscape
        ? Math.round(
            chrome.title * PAYTABLE_TUNED_LANDSCAPE_CHROME.headerTitleMul,
          )
        : chrome.title;

  const slideTitleFontSize = isWideDesktop
    ? Math.round(
        chrome.subtitle *
          PAYTABLE_TUNED_LANDSCAPE_CHROME.wideDesktopSlideTitleMul,
      )
    : isDesktop
    ? Math.round(
        chrome.subtitle * PAYTABLE_TUNED_LANDSCAPE_CHROME.desktopSlideTitleMul,
      )
    : isTunedMobileLandscape
      ? Math.round(
          chrome.subtitle * PAYTABLE_TUNED_LANDSCAPE_CHROME.slideTitleMul,
        )
      : chrome.subtitle;

  const featureSubtitleFontSize = isTunedMobileLandscape
    ? getUniformFeatureSubtitleH()
    : Math.round(chrome.subtitle * 0.7 * smallMobilePortraitSubtitleMul);

  const glowTop = isTunedStandardPortrait ? "-12%" : "-8%";
  const glowWidth = isTunedStandardPortrait ? "150%" : "120%";

  const arrowLayout = getPaytableArrowLayout(chrome);
  const arrowSafeZone = arrowLayout.safeGutter * 2;

  const handleClose = () => {
    soundManager.play(SOUND_NAME.button_click);
    dispatch(closePaytable());
    dispatch(closeMenu());
  };

  // ── Build slides array ──
  // Each entry is a render function () => JSX. The slider wraps each in <PaytableSlide>.

  // Extract wild symbol image from spine (cached after first extraction)
  const wildImgSrc = useMemo(() => {
    const dataURLs = SpineTextureExtractor.getDataURLs();
    if (!dataURLs) return null;
    return dataURLs.w0?.src || null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldRender]);

  // Extract all spine data URLs (used by slide 3 for mid/low pay symbols)
  const spineDataURLs = useMemo(() => {
    return SpineTextureExtractor.getDataURLs() || {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldRender]);

  // Build symbol grid data for slide 3: mid pay (hp+mp = 4) + low pay (5)
  const symbolGridData = useMemo(() => {
    const groups = getSymbolGroups();
    const buildSection = (symbols) =>
      symbols.map((s) => ({
        id: s.id,
        name: s.name,
        imgSrc: spineDataURLs[s.name]?.src || null,
        imgW: spineDataURLs[s.name]?.width || 0,
        imgH: spineDataURLs[s.name]?.height || 0,
        payValues: getPayValues(paytable, s.id, bet, baseBet),
      }));

    return {
      midPay: buildSection([...groups.hp, ...groups.mp]),
      lowPay: buildSection(groups.lp),
    };
  }, [spineDataURLs, paytable, bet, baseBet]);

  // ── Game image cache — extract all data URLs once when overlay mounts ──
  // Same pattern as primary PaytableSection.symbolImages: useMemo runs once,
  // all slides read from the map — zero canvas work during slider transitions.
  const gameImages = useMemo(() => {
    if (!shouldRender) return {};
    const names = [
      "zeus_respins",
      "zeus_respins_features",
      // Feature slides — composited from split pot/coins images
      "intro_feature_collect_pot",
      "intro_feature_collect_coins",
      "intro_feature_double_pot",
      "intro_feature_double_coins",
      "intro_feature_mystery_pot",
      "intro_feature_mystery_coins",
      "grand_jackpot",
      "coin_symbol",
      "jackpot_coin_symbols",
      "blue_zeus_symbol",
      "green_zeus_symbol",
      "red_zeus_symbol",
      "paylines_portrait",
      "paylines_landscape",
      "glow",
    ];
    const result = {};
    for (const name of names) {
      result[name] = getGameImageSrc(name);
    }
    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldRender]);

  // Payline image — picked from cache based on current orientation
  const paylineImgSrc =
    gameImages[isPortrait ? "paylines_portrait" : "paylines_landscape"] || "";

  // ── Dynamic font size for feature descriptions ──
  // Single path for all devices — ratios from DESC_FONT_CONFIG.
  const fontCfg = getDeviceConfig(DESC_FONT_CONFIG, layoutDevice, orientation);
  const smallMobilePortraitDescMul = isSmallMobilePortrait
    ? SMALL_MOBILE_PORTRAIT_TYPO.descFontMul
    : 1;
  const descBaseFontSize = Math.round(
    chrome.base * fontCfg.baseMul * smallMobilePortraitDescMul,
  );
  // Small-mobile-landscape description shrink — shared by the feature slides
  // (baked into uniformDescFontSize) and the symbol slides (wild, slide 1) so
  // every description renders at the same size on this breakpoint.
  const smallMobileLandscapeDescMul = isSmallMobileLandscape
    ? (landscapeUniformTypo?.smallMobileLandscapeDescFontMul ?? 1)
    : 1;
  const uniformDescFontSize = landscapeUniformTypo
    ? Math.round(
        descBaseFontSize *
          landscapeUniformTypo.descFontMul *
          smallMobileLandscapeDescMul,
      )
    : null;
  // ── Refs for measuring feature slide body containers (portrait + landscape) ──
  const featureBodyRefs = useRef([]);
  const [featureFontSizes, setFeatureFontSizes] = useState(null);

  // ── Dynamic feature text sizing — measures actual container from DOM on every resize ──
  // Uses useLayoutEffect to read real container dimensions from refs, then steps down
  // font size until text fits. Runs before paint so there's no visual flash.
  useLayoutEffect(() => {
    if (!shouldRender) {
      setFeatureFontSizes((prev) => (prev === null ? prev : null));
      return;
    }

    const hasRefs = featureBodyRefs.current.some((el) => el != null);
    if (!hasRefs) return;

    const canvas = getMeasureCanvas();
    const ctx = canvas.getContext("2d");
    const lp = Math.round(chrome.title * 0.5);

    const newSizes = FEATURE_SLIDES.map((f, idx) => {
      const bodyEl = featureBodyRefs.current[idx];
      const baseFont = Math.round(
        descBaseFontSize * getSlideDescFontMul(f.descFontMul),
      );

      if (!bodyEl) {
        return {
          fontSize: isPortrait
            ? baseFont
            : landscapeFixedDescFont
              ? uniformDescFontSize
              : descBaseFontSize,
          descWidths: [],
        };
      }

      // Small mobile landscape keeps the fixed uniform desc size (its
      // uniformDescFontSize already bakes in the extra shrink). Standard
      // mobile landscape, tablet landscape, and desktop fall through to the
      // measure-and-fit path below so long translations can't overflow.
      if (!isPortrait && isSmallMobileLandscape && uniformDescFontSize != null) {
        return { fontSize: uniformDescFontSize, descWidths: [] };
      }

      const descText = t(f.desc);
      let textW;
      let textAvailH;

      if (isPortrait) {
        // Stacked layout: subtitle → image → description (measure image from DOM).
        const stackedBodyEl = bodyEl.querySelector(".spt-feature-stacked-body");
        const imgWrapEl = stackedBodyEl?.querySelector(".spt-feature-img-wrap");
        const imageH = imgWrapEl?.offsetHeight ?? 0;
        const stackedBodyH = stackedBodyEl?.clientHeight ?? bodyEl.clientHeight;
        textW = bodyEl.clientWidth;
        textAvailH = Math.max(0, stackedBodyH - imageH - lp);
      } else {
        // Side-by-side layout: image left, subtitle + description right.
        const bodyH = bodyEl.clientHeight;
        const bodyW = bodyEl.clientWidth;
        const textWrapEl = bodyEl.querySelector(".spt-feature-text-wrap");
        const descPad = chrome.base * 0.3;
        textW = textWrapEl
          ? textWrapEl.clientWidth
          : (bodyW * 0.5 - descPad * 2) * 0.85;

        const subtitleEl = bodyEl.querySelector(".spt-feature-subtitle");
        const subtitleH =
          subtitleEl?.offsetHeight ??
          Math.round(getUniformFeatureSubtitleH(1.2));
        const paddingTop = isTunedMobileLandscape
          ? 0
          : Math.round(lp * (f.textPadMul != null ? f.textPadMul : 1.5));
        const textWrapGap = isTunedMobileLandscape
          ? Math.round(lp * 0.4)
          : Math.round(lp * 0.5);
        textAvailH = Math.max(
          0,
          bodyH - subtitleH - paddingTop - textWrapGap,
        );
      }

      // Fit-to-height (same approach as wild / coin slides): start at the shared
      // base size, then step down until the description fits.
      const fillSafety = isPortrait
        ? FEATURE_DESC_FILL_SAFETY_PORTRAIT
        : FEATURE_DESC_FILL_SAFETY;
      const maxTextH = textAvailH * fillSafety;
      let fontSize = baseFont;
      let designTextH = measureTextHeight(ctx, descText, fontSize, textW);
      while (designTextH > maxTextH && fontSize > 6) {
        fontSize -= 1;
        designTextH = measureTextHeight(ctx, descText, fontSize, textW);
      }
      // Portrait: canvas wrapping is slightly taller than the DOM — reclaim 1px
      // when the full container height can still fit it (avoids over-shrinking).
      if (isPortrait && fontSize < baseFont) {
        const bumped = fontSize + 1;
        const bumpedH = measureTextHeight(ctx, descText, bumped, textW);
        if (bumpedH <= textAvailH) {
          fontSize = bumped;
        }
      }

      return { fontSize, descWidths: [] };
    });

    // Only update state if values actually changed (prevents infinite re-render loop)
    setFeatureFontSizes((prev) => {
      if (
        prev &&
        prev.length === newSizes.length &&
        prev.every(
          (v, i) =>
            v.fontSize === newSizes[i].fontSize &&
            JSON.stringify(v.descWidths) ===
              JSON.stringify(newSizes[i].descWidths),
        )
      ) {
        return prev;
      }
      return newSizes;
    });
  }, [
    shouldRender,
    isPortrait,
    isSmallMobileLandscape,
    isTunedMobileLandscape,
    dimensions,
    descBaseFontSize,
    uniformDescFontSize,
    landscapeFixedDescFont,
    chrome,
    t,
    uniformFeatureSubtitleMul,
    getUniformFeatureSubtitleH,
    getSlideDescFontMul,
    measureTick,
  ]);

  // ── Refs for measuring slide content containers ──
  const wildContentRef = useRef(null);
  const slide1SectionsRef = useRef(null);
  const zeusContentRef = useRef(null);
  const gridContentRef = useRef(null);
  const [slideSizes, setSlideSizes] = useState(null);

  // ── Dynamic sizing for slides 0–3 — measures actual containers from DOM on every resize ──
  useLayoutEffect(() => {
    if (!shouldRender) return;

    const canvas = getMeasureCanvas();
    const ctx = canvas.getContext("2d");
    const lp = Math.round(chrome.title * 0.5);

    // ── Wild slide (slide 0) — uniform scale, integer pixels ──
    // One scale factor controls everything; applied as rounded px values (no transform blur).
    let wild = {
      imgH: 100,
      fontSize: Math.round(
        descBaseFontSize * getSlideDescFontMul(WILD_SLIDE.descFontMul),
      ),
    };
    if (wildContentRef.current) {
      const el = wildContentRef.current;
      const containerH = el.clientHeight;
      const textW = isPortrait ? el.clientWidth * 0.75 : el.clientWidth * 0.6;

      const imgFraction = isPortrait
        ? (WILD_SLIDE.imgFraction?.portrait ?? 0.28)
        : (WILD_SLIDE.imgFraction?.landscape ?? 0.38);
      const designImgH = Math.round(containerH * imgFraction);

      const designSubtitleH = getUniformFeatureSubtitleH();
      const designDescFont = Math.round(
        descBaseFontSize * getSlideDescFontMul(WILD_SLIDE.descFontMul),
      );
      const textH = measureTextHeight(
        ctx,
        t(WILD_SLIDE.desc),
        designDescFont,
        textW,
      );
      const gap = Math.round(lp * 0.6);
      // Only the image + description scale; the subtitle and gaps render at a
      // fixed size. Fit the scalable parts into the height that remains after
      // that fixed overhead — scaling the whole stack overflows, because the
      // fixed parts don't shrink with it. Small mobile keeps its prior formula
      // (scale the whole stack) byte-for-byte.
      const fixedOverhead = designSubtitleH * 1.3 + gap * 2;
      const scalableH = designImgH + textH;
      const scale = isSmallMobileLandscape
        ? Math.min(1, containerH / (scalableH + fixedOverhead))
        : Math.min(
            1,
            Math.max(0, containerH * FEATURE_DESC_FILL_SAFETY - fixedOverhead) /
              Math.max(1, scalableH),
          );
      const scaledFontSize = isSmallMobileLandscape
        ? Math.round(designDescFont * smallMobileLandscapeDescMul)
        : Math.round(designDescFont * scale);
      wild = {
        imgH: Math.round(designImgH * scale),
        fontSize: scaledFontSize,
        descWidth: Math.round(scaledFontSize * 22),
      };
    }

    // ── Coin + Jackpot slide (slide 1) — uniform scale, integer pixels ──
    let slide1 = {
      imgH: 100,
      fontSize: Math.round(
        descBaseFontSize *
          getSlideDescFontMul(SYMBOL_SLIDE_1_CONFIG.descFontMul),
      ),
    };
    if (slide1SectionsRef.current) {
      const el = slide1SectionsRef.current;
      const containerH = el.clientHeight;
      const containerW = el.clientWidth;
      const numSections = SYMBOL_SLIDE_1.length;

      let sectionH, textW;
      if (isPortrait) {
        const betweenGaps = Math.round(lp * 1.6) * (numSections - 1);
        sectionH = (containerH - betweenGaps) / numSections;
        textW = containerW * 0.75;
      } else if (isTunedMobileLandscape) {
        sectionH = containerH;
        const sectionGap = lp;
        textW = (containerW - sectionGap) / numSections;
      } else {
        sectionH = containerH;
        const descPad = chrome.base * 0.3 * 2;
        textW = (containerW / numSections - descPad) * 0.6;
      }

      const imgFraction = isPortrait
        ? (SYMBOL_SLIDE_1_CONFIG.imgFraction?.portrait ?? 0.5)
        : isTunedMobileLandscape
          ? (SYMBOL_SLIDE_1_CONFIG.imgFraction?.landscape ?? 0.38) *
            (SYMBOL_SLIDE_1_CONFIG.standardMobileLandscapeImgFractionMul ?? 1)
          : (SYMBOL_SLIDE_1_CONFIG.imgFraction?.landscape ?? 0.38);
      const designImgH = Math.round(sectionH * imgFraction);
      const designSubtitleH = getUniformFeatureSubtitleH();
      const designDescFont = Math.round(
        descBaseFontSize *
          getSlideDescFontMul(SYMBOL_SLIDE_1_CONFIG.descFontMul),
      );
      const gap = Math.round(lp * 0.6);

      const slide1ImgScaleMul = SYMBOL_SLIDE_1_CONFIG.imgScaleMul ?? 1;
      const maxDesignImgH = Math.max(
        ...SYMBOL_SLIDE_1.map((s) => {
          const sc =
            ((typeof s.imgScale === "object"
              ? getImgWidth(s.imgScale, layoutDevice, isPortrait)
              : s.imgScale) || 1) * slide1ImgScaleMul;
          return Math.round(designImgH * sc);
        }),
      );

      // Only image + description scale; subtitle + gaps are fixed. Fit the
      // scalable parts into the height remaining after that fixed overhead
      // (see the Wild slide above for why scaling the whole stack overflows).
      const fixedOverhead = designSubtitleH * 1.3 + gap * 2;
      const perSection = SYMBOL_SLIDE_1.map((s) => {
        const textH = measureTextHeight(ctx, t(s.desc), designDescFont, textW);
        const scalableH = maxDesignImgH + textH;
        return isSmallMobileLandscape
          ? Math.min(1, sectionH / (scalableH + fixedOverhead))
          : Math.min(
              1,
              Math.max(0, sectionH * FEATURE_DESC_FILL_SAFETY - fixedOverhead) /
                Math.max(1, scalableH),
            );
      });
      const scale = Math.min(...perSection);

      const scaledFontSize = isSmallMobileLandscape
        ? Math.round(designDescFont * smallMobileLandscapeDescMul)
        : Math.round(designDescFont * scale);
      slide1 = {
        imgH: Math.round(designImgH * scale),
        fontSize: scaledFontSize,
        ...(isTunedMobileLandscape
          ? {}
          : {
              descWidths: [
                Math.round(scaledFontSize * 18),
                Math.round(scaledFontSize * 22),
              ],
            }),
      };
    }

    // ── Zeus Symbols slide (slide 2) — integer pixels, font-stepping ──
    // Layout: image LEFT + text RIGHT (horizontal), so image doesn't scale with font.
    const slide2DescFontMul = getSlideDescFontMul(
      SYMBOL_SLIDE_2_CONFIG.descFontMul,
    );
    const slide2DescMultipliers =
      SYMBOL_SLIDE_2_CONFIG.descWidthMultipliers ?? [15, 15, 15];

    let slide2 = {
      imgMaxH: 80,
      imgW: 100,
      fontSize: Math.round(descBaseFontSize * slide2DescFontMul),
      headerDescFontSize: Math.round(descBaseFontSize * slide2DescFontMul),
      sectionH: 100,
      headerDescWidth: undefined,
      descWidths: [],
    };
    if (zeusContentRef.current) {
      const el = zeusContentRef.current;
      const availH = el.clientHeight;
      const availW = el.clientWidth;
      const numSections = SYMBOL_SLIDE_2.length;

      // Measure the header area (subtitle + desc) that's already rendered above the sections
      const subtitleEl = el.querySelector(".spt-feature-subtitle");
      const headerDescEl = el.querySelector(".spt-zeus-header-desc");
      const zeusLayoutCtx = {
        isStandardMobilePortrait: isTunedStandardPortrait,
        isSmallMobilePortrait,
      };
      const zeusContentSizingGap = Math.round(
        lp *
          (isPortrait
            ? (SYMBOL_SLIDE_2_CONFIG.contentGapMul ?? 0.8)
            : (SYMBOL_SLIDE_2_CONFIG.contentSizingGapMul ??
              SYMBOL_SLIDE_2_CONFIG.contentGapMul ??
              0.8)),
      );
      const zeusSectionRowSizingGap = Math.round(
        lp *
          (isPortrait
            ? (SYMBOL_SLIDE_2_CONFIG.sectionRowGapMul ?? 0.8)
            : (SYMBOL_SLIDE_2_CONFIG.sectionRowSizingGapMul ??
              SYMBOL_SLIDE_2_CONFIG.sectionRowGapMul ??
              0.8)),
      );
      const zeusInnerGap = Math.round(
        lp * (SYMBOL_SLIDE_2_CONFIG.sectionInnerGapMul ?? 0.8),
      );
      const imgHeightMul = getZeusSlideLayoutVal(
        SYMBOL_SLIDE_2_CONFIG.imgHeightMul,
        zeusLayoutCtx,
        0.65,
      );
      const maxTextHeightMul = getZeusSlideLayoutVal(
        SYMBOL_SLIDE_2_CONFIG.maxTextHeightMul,
        zeusLayoutCtx,
        0.85,
      );
      const textMeasureWidthMul = getZeusSlideLayoutVal(
        SYMBOL_SLIDE_2_CONFIG.textMeasureWidthMul,
        zeusLayoutCtx,
        0.8,
      );
      const portraitFixedFont = isPortrait
        ? getZeusSlideLayoutVal(
            SYMBOL_SLIDE_2_CONFIG.portraitFixedFont,
            zeusLayoutCtx,
            true,
          )
        : false;
      const portraitFontScale = getZeusSlideLayoutVal(
        SYMBOL_SLIDE_2_CONFIG.portraitFontScale,
        zeusLayoutCtx,
        1,
      );
      const designFontSize = Math.round(descBaseFontSize * slide2DescFontMul);
      const headerDescDesignSize = Math.round(
        designFontSize * (portraitFixedFont ? portraitFontScale : 1),
      );

      // Available height for the sections grid = total minus header elements + content gaps
      const subtitleH =
        subtitleEl?.offsetHeight || getUniformFeatureSubtitleH();
      const headerDescH = portraitFixedFont
        ? measureTextHeight(
            ctx,
            t(SYMBOL_SLIDE_2_HEADER.desc),
            headerDescDesignSize,
            availW,
          )
        : headerDescEl?.offsetHeight || 0;
      const headerOverhead = subtitleH + headerDescH + zeusContentSizingGap * 2;
      const sectionsAvailH = Math.max(0, availH - headerOverhead);

      let sectionH, sectionW;
      const imgRatio = getImgWidth(
        SYMBOL_SLIDE_2_IMG_RATIO,
        layoutDevice,
        isPortrait,
      );
      const innerGap = zeusInnerGap;

      if (isPortrait) {
        const betweenGaps = zeusSectionRowSizingGap * (numSections - 1);
        sectionH = Math.round(
          ((sectionsAvailH - betweenGaps) / numSections) * 0.98,
        );
        sectionW = availW;

        const imgW = Math.round(sectionW * imgRatio);
        const textW = (sectionW - imgW - innerGap) * textMeasureWidthMul;
        const maxTextH = sectionH * maxTextHeightMul;
        const perSection = SYMBOL_SLIDE_2.map((s) => {
          const textH = measureTextHeight(
            ctx,
            t(s.desc),
            designFontSize,
            textW,
          );
          return Math.min(1, maxTextH / textH);
        });
        const fitScale = Math.min(...perSection);
        const fontSize = portraitFixedFont
          ? Math.round(designFontSize * portraitFontScale)
          : Math.round(designFontSize * fitScale);
        const imgMaxH = Math.max(0, Math.round(sectionH * imgHeightMul));
        const headerDescFontSize = Math.round(
          designFontSize * (portraitFixedFont ? portraitFontScale : fitScale),
        );

        slide2 = {
          imgMaxH,
          imgW,
          fontSize,
          headerDescFontSize,
          sectionH,
          headerDescWidth: undefined,
          descWidths: slide2DescMultipliers.map((m) =>
            Math.round(fontSize * m),
          ),
        };
      } else {
        // Landscape: uniform-scale a fixed design. The content is laid out once
        // at SYMBOL_SLIDE_2_CONFIG.landscapeDesign px sizes (fixed font + fixed
        // widths → identical line-breaks on every landscape device), then the
        // whole block is transform:scale()'d to fit the container. Natural size
        // is read via offsetWidth/Height, which ignore the CSS transform, so the
        // measurement is stable regardless of the scale currently applied.
        const design = SYMBOL_SLIDE_2_CONFIG.landscapeDesign;
        const contentEl = el.querySelector(".spt-zeus-content");
        // The white subtitle is an unscaled sibling above the scaled block, so
        // reserve its height (+ the standard subtitle gap, matching
        // featureTextWrapGap = lp*0.4) before fitting the rest.
        const subH = subtitleEl?.offsetHeight || 0;
        const wrapGap = Math.round(lp * 0.4);
        const contentAvailH = Math.max(0, availH - subH - wrapGap);
        const naturalW = contentEl?.offsetWidth || design.width;
        const naturalH = contentEl?.offsetHeight || 0;
        const scaleCap = design.scaleCap ?? 2.0;
        const rawScale =
          naturalH > 0
            ? Math.min(availW / naturalW, contentAvailH / naturalH)
            : 1;
        const scale = Math.max(0.1, Math.min(scaleCap, rawScale));
        slide2 = { scale };
      }
    }

    // ── Regular Grid slide (slide 3) — integer pixels, continuous scale ──
    let slide3 = {
      imgMaxH: 60,
      lpImgMaxH: 50,
      pvFontSize: 12,
      pvFontWeight: 600,
    };
    if (gridContentRef.current) {
      const el = gridContentRef.current;
      const availH = el.clientHeight;
      const availW = el.clientWidth;

      if (isPortrait) {
        const blockGap = Math.round(lp * 0.3);
        const portraitRows = 4;
        const totalGaps = blockGap * (portraitRows - 1);
        const rowH = Math.round(
          Math.max(0, (availH - totalGaps) / portraitRows),
        );

        const basePvFont = Math.round(chrome.base * 0.82);
        const pvFontSize =
          basePvFont + (SYMBOL_SLIDE_4_CONFIG.portraitPvFontSizeOffset ?? 0);
        const pvBlockH = Math.round(pvFontSize * 1.2) * 3;
        const imgMaxH = Math.round(Math.max(0, rowH - pvBlockH));
        const lpImgMaxH = Math.round(imgMaxH * 0.65);

        slide3 = {
          imgMaxH,
          lpImgMaxH,
          pvFontSize,
          pvFontWeight: SYMBOL_SLIDE_4_CONFIG.portraitPvFontWeight ?? 400,
        };
      } else {
        const sectionGap = lp;
        const cellInternalGap = Math.round(lp * 0.1);
        const pvLineHeight = 1.3;
        const mpMaxLines = 4;
        const lpMaxLines = 3;

        const designPvFont = Math.round(chrome.base * 0.84);
        const designRowBudget = Math.round(
          Math.max(0, (availH - sectionGap) / 2),
        );
        const designUsableRowH = Math.max(0, designRowBudget - cellInternalGap);

        const gridLandscapeImgMul =
          SYMBOL_SLIDE_4_CONFIG.landscapeImgHeightMul ?? 0.72;
        const designImgMaxH = Math.round(
          designUsableRowH * gridLandscapeImgMul,
        );
        const designLpImgMaxH = Math.round(designImgMaxH * 0.9);

        const designRow1H =
          designImgMaxH +
          cellInternalGap +
          designPvFont * pvLineHeight * mpMaxLines;
        const designRow2H =
          designLpImgMaxH +
          cellInternalGap +
          designPvFont * pvLineHeight * lpMaxLines;
        const designTotalH = designRow1H + sectionGap + designRow2H;

        const smallLandscapeGridMul = isSmallMobileLandscape
          ? (SYMBOL_SLIDE_4_CONFIG.smallMobileLandscapeScaleMul ?? 1)
          : 1;
        const fillScaleCap = SYMBOL_SLIDE_4_CONFIG.landscapeFillScaleCap ?? 1.3;

        // Width fit — the height fill alone lets symbols balloon on tall/square
        // viewports (e.g. tablet landscape) so the fixed nowrap rows overflow.
        // Cap the scale so each row's images also fit availW, mirroring how the
        // game symbols scale to min(width-fit, height-fit). Driven by the
        // (square) image width only — the pay-value text sits under each symbol
        // and isn't the binding element, so folding it in here would needlessly
        // crush the font.
        const sectionCellGap = Math.round(lp * 1.5); // matches .spt-grid-section gap
        const widthScaleForRow = (cells, designImgW) => {
          const n = cells.length;
          if (n === 0 || designImgW <= 0) return Infinity;
          const avail = Math.max(0, availW - sectionCellGap * (n - 1));
          return avail / (n * designImgW);
        };
        const widthScale = Math.min(
          widthScaleForRow(symbolGridData.midPay, designImgMaxH),
          widthScaleForRow(symbolGridData.lowPay, designLpImgMaxH),
        );

        const scale =
          designTotalH > 0
            ? Math.min(fillScaleCap, availH / designTotalH, widthScale) *
              smallLandscapeGridMul
            : smallLandscapeGridMul;

        const imgMaxH = Math.round(designImgMaxH * scale);
        const lpImgMaxH = Math.round(designLpImgMaxH * scale);
        const pvFontSize = Math.round(designPvFont * scale);

        slide3 = { imgMaxH, lpImgMaxH, pvFontSize, pvFontWeight: 600 };
      }
    }

    // Only update state if values changed (prevents infinite loop)
    setSlideSizes((prev) => {
      const next = { wild, slide1, slide2, slide3 };
      if (prev) {
        const same = JSON.stringify(prev) === JSON.stringify(next);
        if (same) return prev;
      }
      return next;
    });
  }, [
    shouldRender,
    dimensions.width,
    dimensions.height,
    isPortrait,
    descBaseFontSize,
    chrome,
    device,
    layoutDevice,
    isMobileSmall,
    isSmallMobileLandscape,
    isTunedStandardPortrait,
    isTunedMobileLandscape,
    isSmallMobilePortrait,
    landscapeFixedDescFont,
    smallMobileLandscapeDescMul,
    uniformDescFontMul,
    uniformFeatureSubtitleMul,
    uniformDescFontSize,
    t,
    symbolGridData,
    getSlideDescFontMul,
    getUniformFeatureSubtitleH,
    measureTick,
  ]);

  const slides = useMemo(
    () =>
      SLIDE_TITLES.map((title, i) => () => (
        <div
          className={`spt-slide-content${i === 2 || (i >= 4 && i <= 9) ? " spt-slide-content-spaced" : ""}`}
          key={`slide-${i}`}
        >
          <div className="spt-slide-title">{t(title)}</div>
          {/* ── Slide 1: Wild Symbol (solo, centered) ── */}
          {i === 0 &&
            (() => {
              const sizes = slideSizes?.wild || {
                imgH: 100,
                fontSize: Math.round(
                  descBaseFontSize *
                    getSlideDescFontMul(WILD_SLIDE.descFontMul),
                ),
              };
              return (
                <div
                  className="spt-symbol-sections spt-wild-solo"
                  ref={wildContentRef}
                  style={{
                    overflow: "hidden",
                    ...(isPortrait && WILD_SLIDE.sectionMarginTop
                      ? {
                          marginTop: getImgWidth(
                            WILD_SLIDE.sectionMarginTop,
                            layoutDevice,
                            isPortrait,
                          ),
                        }
                      : {}),
                  }}
                >
                  <div className="spt-symbol-section">
                    <div
                      className="spt-symbol-img-wrap"
                      style={{ height: sizes.imgH }}
                    >
                      {wildImgSrc && (
                        <img
                          className="spt-symbol-img"
                          src={wildImgSrc}
                          alt={t(WILD_SLIDE.subtitle)}
                          style={{ height: "100%", objectFit: "contain" }}
                        />
                      )}
                    </div>
                    <div className="spt-feature-subtitle">
                      {t(WILD_SLIDE.subtitle)}
                    </div>
                    <p
                      className="spt-feature-desc"
                      style={{
                        fontSize: `${sizes.fontSize}px`,
                        width:
                          !isPortrait && sizes.descWidth
                            ? sizes.descWidth
                            : undefined,
                      }}
                    >
                      {t(WILD_SLIDE.desc)}
                    </p>
                  </div>
                </div>
              );
            })()}
          {/* ── Slide 2: Coin + Jackpot Coins ── */}
          {i === 1 &&
            (() => {
              const sizes = slideSizes?.slide1 || {
                imgH: 100,
                fontSize: Math.round(
                  descBaseFontSize *
                    getSlideDescFontMul(SYMBOL_SLIDE_1_CONFIG.descFontMul),
                ),
              };
              return (
                <div
                  className="spt-symbol-sections spt-symbol-sections-coins"
                  ref={slide1SectionsRef}
                  style={{ overflow: "hidden" }}
                >
                  {(() => {
                    const slide1ImgScaleMul =
                      SYMBOL_SLIDE_1_CONFIG.imgScaleMul ?? 1;
                    const maxImgH = Math.max(
                      ...SYMBOL_SLIDE_1.map((s) => {
                        const sc =
                          ((typeof s.imgScale === "object"
                            ? getImgWidth(s.imgScale, layoutDevice, isPortrait)
                            : s.imgScale) || 1) * slide1ImgScaleMul;
                        return Math.round(sizes.imgH * sc);
                      }),
                    );
                    return SYMBOL_SLIDE_1.map((s, si) => {
                      const imgSrc = s.assetName
                        ? gameImages[s.assetName]
                        : s.img;
                      const vis = s.visualScale || 1;
                      const descW =
                        !isTunedMobileLandscape &&
                        !isPortrait &&
                        sizes.descWidths
                          ? sizes.descWidths[si]
                          : undefined;
                      return (
                        <div className="spt-symbol-section" key={si}>
                          <div
                            className="spt-symbol-img-wrap"
                            style={{ height: maxImgH }}
                          >
                            {imgSrc && (
                              <img
                                className="spt-symbol-img"
                                src={imgSrc}
                                alt={t(s.subtitle)}
                                style={{
                                  height: "100%",
                                  objectFit: "contain",
                                  transform:
                                    vis !== 1 ? `scale(${vis})` : undefined,
                                }}
                              />
                            )}
                          </div>
                          <div className="spt-feature-subtitle">
                            {t(s.subtitle)}
                          </div>
                          <p
                            className="spt-feature-desc"
                            style={{
                              fontSize: `${sizes.fontSize}px`,
                              width: descW,
                            }}
                          >
                            {t(s.desc)}
                          </p>
                        </div>
                      );
                    });
                  })()}
                </div>
              );
            })()}
          {/* ── Slide 3: Zeus Symbols and POTs ── */}
          {i === 2 &&
            (() => {
              const design = SYMBOL_SLIDE_2_CONFIG.landscapeDesign;
              const sizes = slideSizes?.slide2 || {
                imgMaxH: 80,
                imgW: 100,
                fontSize: Math.round(
                  descBaseFontSize *
                    getSlideDescFontMul(SYMBOL_SLIDE_2_CONFIG.descFontMul),
                ),
                headerDescFontSize: Math.round(
                  descBaseFontSize *
                    getSlideDescFontMul(SYMBOL_SLIDE_2_CONFIG.descFontMul),
                ),
                sectionH: 100,
              };
              // Landscape renders the fixed design (deterministic breaks) and is
              // transform-scaled to fit by the fit effect. Portrait keeps its
              // existing per-element sizing.
              const zeusScale = sizes.scale ?? 1;
              const headerDescFontSize = isPortrait
                ? sizes.headerDescFontSize
                : design.headerDescFont;
              const headerDescWidth = isPortrait
                ? sizes.headerDescWidth || undefined
                : Math.round(design.width * design.headerDescWidthPct);
              const descFontSize = isPortrait ? sizes.fontSize : design.descFont;
              // The white title stays at the normal subtitle size and is NOT
              // scaled — in landscape it's a sibling above the scaled block.
              const subtitleEl = (
                <div className="spt-feature-subtitle">
                  {t(SYMBOL_SLIDE_2_HEADER.subtitle)}
                </div>
              );
              return (
                <div className="spt-zeus-slide-wrap" ref={zeusContentRef}>
                  {!isPortrait && subtitleEl}
                  <div
                    className="spt-zeus-content"
                    style={
                      isPortrait
                        ? undefined
                        : {
                            width: `${design.width}px`,
                            transform: `scale(${zeusScale})`,
                          }
                    }
                  >
                    {isPortrait && subtitleEl}
                    <p
                      className="spt-feature-desc spt-zeus-header-desc"
                      style={{
                        fontSize: `${headerDescFontSize}px`,
                        width: headerDescWidth,
                      }}
                    >
                      {t(SYMBOL_SLIDE_2_HEADER.desc)}
                    </p>
                    <div className="spt-zeus-sections">
                      {SYMBOL_SLIDE_2.map((s, si) => (
                        <div className="spt-zeus-section" key={si}>
                          <div className="spt-zeus-img-wrap">
                            <img
                              className="spt-zeus-img"
                              src={
                                s.assetName ? gameImages[s.assetName] : s.img
                              }
                              alt={`Zeus ${si}`}
                            />
                          </div>
                          <p
                            className="spt-feature-desc spt-zeus-desc"
                            style={{
                              fontSize: `${descFontSize}px`,
                              maxWidth:
                                !isPortrait && sizes.descWidths?.[si]
                                  ? sizes.descWidths[si]
                                  : undefined,
                            }}
                          >
                            {t(s.desc)}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })()}
          {/* ── Slide 4: Regular Symbols Grid (mid pay + low pay) ── */}
          {i === 3 &&
            (() => {
              const sizes = slideSizes?.slide3 || {
                imgMaxH: 60,
                lpImgMaxH: 50,
                pvFontSize: 12,
                pvFontWeight: 600,
              };

              const renderGridCell = (sym, symIdx, { isLp = false } = {}) => {
                const imgH = isLp ? sizes.lpImgMaxH : sizes.imgMaxH;

                return (
                  <div className="spt-grid-cell" key={symIdx}>
                    <div className="spt-grid-img-wrap" style={{ height: imgH }}>
                      {sym.imgSrc && (
                        <img
                          className="spt-grid-img"
                          src={sym.imgSrc}
                          alt={sym.name}
                          style={{ maxHeight: imgH, objectFit: "contain" }}
                        />
                      )}
                    </div>
                    <div className="spt-grid-values">
                      {sym.payValues.map((pv, pvIdx) => (
                        <span
                          className="spt-grid-pv"
                          key={pvIdx}
                          style={{
                            fontSize: `${sizes.pvFontSize}px`,
                            fontWeight: sizes.pvFontWeight ?? 600,
                          }}
                        >
                          {pv.count}: {pv.payout} {currency || ""}
                        </span>
                      ))}
                    </div>
                  </div>
                );
              };

              if (isPortrait) {
                const { midPay, lowPay } = symbolGridData;
                const twoColSymbols = [...midPay, ...lowPay.slice(0, 2)];
                const threeColSymbols = lowPay.slice(2, 5);
                return (
                  <div className="spt-grid-content" ref={gridContentRef}>
                    <div className="spt-grid-block spt-grid-block-2col">
                      {twoColSymbols.map((sym, symIdx) =>
                        renderGridCell(sym, symIdx, { isLp: symIdx >= 4 }),
                      )}
                    </div>
                    <div className="spt-grid-block spt-grid-block-3col">
                      {threeColSymbols.map((sym, symIdx) =>
                        renderGridCell(sym, symIdx, { isLp: true }),
                      )}
                    </div>
                  </div>
                );
              }

              return (
                <div className="spt-grid-content" ref={gridContentRef}>
                  {[symbolGridData.midPay, symbolGridData.lowPay].map(
                    (section, si) => {
                      const isLp = si === 1;
                      return (
                        <div
                          className={`spt-grid-section ${isLp ? "spt-grid-lp" : "spt-grid-mp"}`}
                          key={si}
                        >
                          {section.map((sym, symIdx) =>
                            renderGridCell(sym, symIdx, { isLp }),
                          )}
                        </div>
                      );
                    },
                  )}
                </div>
              );
            })()}
          {/* ── Slides 5–10: Feature slides ── */}
          {i >= 4 &&
            i <= 9 &&
            (() => {
              const fi = i - 4;
              const f = FEATURE_SLIDES[fi];
              const featureData = (featureFontSizes &&
                featureFontSizes[fi]) || {
                fontSize: Math.round(
                  descBaseFontSize * getSlideDescFontMul(f.descFontMul),
                ),
                descWidths: [],
              };
              const fontSize = featureData.fontSize;
              // descFontMul is baked into the fit effect — use the returned size directly.
              const descFontSize = fontSize;
              const portraitImgScale = isPortrait
                ? 0.8 * (f.portraitImgMul ?? 1)
                : 1;
              const landscapeImgScale =
                isTunedMobileLandscape && f.landscapeImgMul != null
                  ? f.landscapeImgMul
                  : 1;
              const imgScale = isPortrait
                ? portraitImgScale
                : landscapeImgScale;
              const imgWidth = `calc(${getImgWidth(f.imgWidth, layoutDevice, isPortrait)} * ${imgScale})`;
              const descMarginTop = f.descMarginTop
                ? getImgWidth(f.descMarginTop, layoutDevice, isPortrait)
                : undefined;
              const imgMarginTop = f.imgMarginTop
                ? getImgWidth(f.imgMarginTop, layoutDevice, isPortrait)
                : undefined;
              const stackedBodyMarginTop = f.stackedBodyMarginTop
                ? getImgWidth(f.stackedBodyMarginTop, layoutDevice, isPortrait)
                : undefined;
              const featureDesc = t(f.desc);
              const descStyle = {
                fontSize: `${descFontSize}px`,
                ...(descMarginTop ? { marginTop: descMarginTop } : {}),
              };
              const imgWrapStyle = imgMarginTop
                ? { marginTop: imgMarginTop }
                : undefined;
              const stackedBodyStyle =
                isPortrait && stackedBodyMarginTop
                  ? { marginTop: stackedBodyMarginTop }
                  : undefined;
              const featureBodyStyle =
                !isPortrait && f.landscapeBodyGapMul != null
                  ? { gap: Math.round(linePad * f.landscapeBodyGapMul) }
                  : undefined;

              // Feature image composited from two separate images (pot + coins)
              // stacked directly, plus coinsGapMul as a per-slide fine-tune —
              // avoids shipping a third, redundant combined image asset.
              const splitImages = f.potAssetName ? (
                <div className="spt-feature-split-stack">
                  <img
                    className="spt-feature-img spt-feature-split-img"
                    src={gameImages[f.potAssetName]}
                    alt={t(f.subtitle)}
                    style={{ width: imgWidth }}
                  />
                  <img
                    className="spt-feature-img spt-feature-split-img"
                    src={gameImages[f.coinsAssetName]}
                    alt=""
                    style={{
                      width: imgWidth,
                      marginTop: `calc(${imgWidth} * ${f.coinsGapMul ?? 0})`,
                    }}
                  />
                </div>
              ) : null;

              if (isPortrait) {
                return (
                  <div className="spt-feature-content spt-feature-stacked">
                    <div
                      className="spt-feature-stacked-inner"
                      style={splitImages ? { overflow: "visible" } : undefined}
                      ref={(el) => {
                        featureBodyRefs.current[fi] = el;
                      }}
                    >
                      <div className="spt-feature-subtitle">
                        {t(f.subtitle)}
                      </div>
                      <div
                        className="spt-feature-stacked-body"
                        style={{
                          ...stackedBodyStyle,
                          ...(splitImages ? { overflow: "visible" } : {}),
                        }}
                      >
                        <div
                          className="spt-feature-img-wrap"
                          style={{
                            ...imgWrapStyle,
                            ...(splitImages ? { overflow: "visible" } : {}),
                          }}
                        >
                          {splitImages || (
                            <img
                              className="spt-feature-img"
                              src={
                                f.assetName ? gameImages[f.assetName] : f.img
                              }
                              alt={t(f.subtitle)}
                              style={{ width: imgWidth }}
                            />
                          )}
                        </div>
                        <p className="spt-feature-desc" style={descStyle}>
                          {featureDesc}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              }

              return (
                <div className="spt-feature-content">
                  <div
                    className="spt-feature-body"
                    style={{
                      ...featureBodyStyle,
                      ...(splitImages ? { overflow: "visible" } : {}),
                    }}
                    ref={(el) => {
                      featureBodyRefs.current[fi] = el;
                    }}
                  >
                    <div
                      className="spt-feature-img-wrap"
                      style={{
                        ...imgWrapStyle,
                        ...(splitImages ? { overflow: "visible" } : {}),
                      }}
                    >
                      {splitImages || (
                        <img
                          className="spt-feature-img"
                          src={f.assetName ? gameImages[f.assetName] : f.img}
                          alt={t(f.subtitle)}
                          style={{ width: imgWidth }}
                        />
                      )}
                    </div>
                    <div
                      className="spt-feature-text-wrap"
                      style={{
                        ...(!isTunedMobileLandscape && f.textPadMul != null
                          ? {
                              paddingTop: Math.round(
                                Math.round(chrome.title * 0.5) * f.textPadMul,
                              ),
                            }
                          : {}),
                        ...(f.subtitleNoWrap ? { overflow: "visible" } : {}),
                      }}
                    >
                      <div
                        className="spt-feature-subtitle"
                        style={
                          f.subtitleNoWrap
                            ? { whiteSpace: "nowrap" }
                            : undefined
                        }
                      >
                        {t(f.subtitle)}
                      </div>
                      <p className="spt-feature-desc" style={descStyle}>
                        {featureDesc}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })()}
          {/* ── Slide 11: Paylines ── */}
          {i === 10 && (
            <img
              className="spt-paylines-img"
              src={paylineImgSrc}
              alt="Paylines"
            />
          )}
        </div>
      )),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      paylineImgSrc,
      device,
      layoutDevice,
      isPortrait,
      featureFontSizes,
      descBaseFontSize,
      wildImgSrc,
      slideSizes,
      symbolGridData,
      currency,
    ],
  );

  // ── Memoized overlay stylesheet — shell + slide content styles ──
  const overlayStyles = useMemo(() => {
    const zeusContentGap = isTunedMobileLandscape
      ? linePad
      : Math.round(linePad * (SYMBOL_SLIDE_2_CONFIG.contentGapMul ?? 0.8));
    const symbolSectionsGap = isTunedMobileLandscape
      ? linePad
      : isPortrait
        ? Math.round(linePad * 1.6)
        : Math.round(linePad * 1.5);
    const symbolSectionFlex = isTunedMobileLandscape
      ? "1 1 0"
      : isPortrait
        ? "0 0 auto"
        : "1";
    const symbolSectionsCoinsJustify = isTunedMobileLandscape
      ? "center"
      : "flex-start";
    const zeusSectionRowGap = Math.round(
      linePad * (SYMBOL_SLIDE_2_CONFIG.sectionRowGapMul ?? 0.8),
    );
    const zeusSectionInnerGap = Math.round(
      linePad * (SYMBOL_SLIDE_2_CONFIG.sectionInnerGapMul ?? 0.8),
    );
    // Landscape uses the fixed uniform-scale design (see SYMBOL_SLIDE_2 fit).
    const zeusDesign = SYMBOL_SLIDE_2_CONFIG.landscapeDesign;
    const featureImgWrapPadRight = isTunedMobileLandscape
      ? 0
      : Math.round(linePad * 0.8);
    const featureTextWrapPadTop = isTunedMobileLandscape
      ? 0
      : Math.round(linePad * 1.5);
    const featureTextWrapPadLeft = isTunedMobileLandscape
      ? 0
      : Math.round(linePad * 0.5);
    const featureTextWrapGap = isTunedMobileLandscape
      ? Math.round(linePad * 0.4)
      : Math.round(linePad * 0.5);
    const featureImgWrapJustify = isTunedMobileLandscape
      ? "center"
      : "flex-end";
    const featureBodyOverflow = isTunedMobileLandscape ? "visible" : "hidden";
    const featureImgWrapOverflow = isTunedMobileLandscape
      ? "visible"
      : "hidden";
    const symbolDescPadH = isTunedMobileLandscape
      ? 0
      : Math.round(chrome.base * 0.3);
    const gridPortraitRowGap = Math.round(linePad * 0.3);
    const gridPortraitColGap = Math.round(linePad * 0.7);
    const gridContentGap = isPortrait ? gridPortraitRowGap : linePad;
    const gridCellGap = Math.round(linePad * 0.1);
    return `
    .spt-overlay { font-family: 'Noto Sans', sans-serif; }

    /* ── HEADER ── */
    .spt-header {
      flex-shrink: 0;
      display: flex;
      flex-direction: column;
      align-items: center;
      padding-bottom: ${headerPaddingBottom}px;
      position: relative;
      z-index: 1;
    }
    .spt-header-title-row {
      position: relative;
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .spt-title {
      flex: 1;
      font-size: ${headerTitleFontSize}px; font-weight: 700;
      color: #ffffff; margin: 0; text-align: center; white-space: nowrap;
      text-shadow: 0 2px 6px rgba(0, 0, 0, 0.5);
    }
    .spt-separator {
      ${
        isTunedShellPortrait
          ? `position: relative;
      margin-top: ${separatorMarginTop}px;
      margin-bottom: ${separatorMarginBottom}px;`
          : `position: absolute;
      bottom: 0; left: 50%; transform: translateX(-50%);`
      }
      width: ${separatorLineWidth}px;
      height: ${separatorHeight}px;
      background: linear-gradient(to right, transparent, #ffc102 35%, #ffc102 65%, transparent);
      clip-path: ellipse(50% 50% at 50% 50%);
    }
    .spt-close-btn {
      position: absolute;
      top: 50%; transform: translateY(-50%);
      right: ${closeButtonRight}px;
      width: ${closeButtonSize}px; height: ${closeButtonSize}px;
      background-color: transparent; border: none; cursor: pointer; padding: 0;
      background-size: contain; background-repeat: no-repeat;
      background-position: center; transition: filter 0.2s ease;
      z-index: 1010;
    }
    .spt-close-btn:hover { filter: brightness(2.0); }

    /* Decorative glow — standard mobile portrait */
    .spt-glow {
      position: absolute;
      top: ${glowTop};
      left: 50%;
      transform: translateX(-50%);
      width: ${glowWidth};
      pointer-events: none;
      z-index: 0;
      opacity: 1;
    }
    /* ── SLIDE CONTENT — base layout for all slide types ── */
    .spt-slide-content {
      flex: 1; min-height: 0;
      width: 100%;
      max-width: ${
        (isTabletPortrait ||
          isSmallMobilePortrait ||
          isStandardMobilePortrait) &&
        shellContentWidth != null
          ? `${shellContentWidth}px`
          : isPortrait
            ? `calc(100% - ${arrowSafeZone}px)`
            : "100%"
      };
      min-width: 0;
      margin: 0;
      box-sizing: border-box;
      display: flex; flex-direction: column;
      align-items: center; justify-content: flex-start;
      gap: ${slideContentGap}px;
      position: relative;
      z-index: 1;
    }
    .spt-slide-content-spaced {
      gap: ${slideContentSpacedGap}px;
    }
    .spt-slide-title {
      font-size: ${slideTitleFontSize}px; font-weight: bold;
      color: #ffc102; text-align: center;
      text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
      margin-bottom: 0;
    }

    /* ── Slide content: Paylines ── */
    .spt-paylines-img {
      flex: 1;
      min-height: 0;
      width: 100%;
      object-fit: contain;
    }

    /* ── Slide content: Features ── */
    .spt-feature-content {
      flex: 1;
      min-height: 0;
      width: 100%;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: flex-start;
    }
    .spt-feature-subtitle {
      font-size: ${featureSubtitleFontSize}px;
      font-weight: 700;
      color: #ffffff;
      text-align: center;
      text-shadow: 0 1px 3px rgba(0, 0, 0, 0.6);
      margin: 0;
      flex-shrink: 0;
    }
    .spt-feature-body {
      flex: 1;
      min-height: 0;
      overflow: ${featureBodyOverflow};
      width: 100%;
      display: flex;
      flex-direction: row;
      align-items: stretch;
      justify-content: flex-start;
    }
    .spt-feature-img-wrap {
      position: relative;
      width: 50%;
      height: 100%;
      min-height: 0;
      display: flex;
      align-items: flex-start;
      justify-content: ${featureImgWrapJustify};
      overflow: ${featureImgWrapOverflow};
      padding-right: ${featureImgWrapPadRight}px;
      box-sizing: border-box;
    }
    /* Feature image composited from two split images (pot + coins) — see splitImages in render */
    .spt-feature-split-stack {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: flex-start;
      width: 100%;
      height: 100%;
      min-height: 0;
    }
    .spt-feature-split-img {
      display: block;
    }
    .spt-feature-text-wrap {
      width: 50%;
      height: 100%;
      padding-top: ${featureTextWrapPadTop}px;
      padding-left: ${featureTextWrapPadLeft}px;
      box-sizing: border-box;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: flex-start;
      gap: ${featureTextWrapGap}px;
      overflow: hidden;
    }
    .spt-feature-img {
      object-fit: contain;
      max-height: 100%;
      /* iOS: pin to own pixel-aligned layer so the scaled WebP's bottom edge
         doesn't render a faint 1px seam line against the dark backdrop */
      -webkit-backface-visibility: hidden;
      backface-visibility: hidden;
      transform: translateZ(0);
    }
    .spt-feature-desc {
      /* font-size set inline via descFontSizes */
      font-weight: 500;
      -webkit-text-stroke: 0.3px rgba(255,255,255,0.5);
      color: #ffffff;
      text-align: center;
      line-height: 1.4;
      white-space: pre-line;
      text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
      margin: 0;
      overflow-wrap: break-word;
    }
    .spt-feature-stacked {
      justify-content: flex-start;
    }
    .spt-feature-stacked-inner {
      flex: 1;
      min-height: 0;
      width: 100%;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: flex-start;
      gap: ${linePad}px;
      overflow: hidden;
    }
    .spt-feature-stacked-body {
      flex: 1;
      min-height: 0;
      width: 100%;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: flex-start;
      gap: ${linePad}px;
      overflow: hidden;
    }
    .spt-feature-stacked .spt-feature-img-wrap {
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      width: 100%;
      height: auto;
      padding-right: 0;
    }
    .spt-feature-stacked .spt-feature-img {
      object-fit: contain;
      max-height: none;
    }


    /* ── Slide content: Wild Symbol (solo — reuses symbol-section classes) ── */
    .spt-symbol-sections.spt-wild-solo {
      justify-content: center;
      align-items: ${isPortrait ? "center" : "flex-start"};
    }
    .spt-symbol-sections.spt-wild-solo .spt-symbol-section {
      justify-content: ${isPortrait ? "center" : "flex-start"};
    }

    /* ── Slide content: Symbol sections (slide 2) ── */
    .spt-symbol-sections {
      flex: 1;
      min-height: 0;
      width: 100%;
      display: flex;
      flex-direction: ${isPortrait ? "column" : "row"};
      align-items: ${isPortrait ? "center" : "flex-start"};
      justify-content: center;
      gap: ${symbolSectionsGap}px;
      overflow: hidden;
    }
    .spt-symbol-sections-coins {
      justify-content: ${symbolSectionsCoinsJustify};
    }
    .spt-symbol-section {
      flex: ${symbolSectionFlex};
      min-height: 0;
      ${isPortrait ? "width: 100%;" : "min-width: 0;"}
      ${isTunedMobileLandscape && !isPortrait ? "width: 0;" : ""}
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: ${isPortrait ? "center" : "flex-start"};
      text-align: center;
      gap: ${Math.round(linePad * 0.4)}px;
      overflow: hidden;
    }
    .spt-symbol-img-wrap {
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      /* height set inline by symbolSlide1Sizes */
    }
    .spt-symbol-img {
      max-height: 100%;
      max-width: 100%;
      object-fit: contain;
    }
    .spt-symbol-section .spt-feature-desc {
      padding: 0 ${symbolDescPadH}px;
    }
    .spt-symbol-section .spt-feature-subtitle {
      white-space: nowrap;
    }

    /* ── Slide 2: Zeus Symbols and POTs ── */
    .spt-zeus-slide-wrap {
      flex: 1;
      min-height: 0;
      width: 100%;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: flex-start;
      overflow: hidden;
      ${isPortrait ? "" : `gap: ${featureTextWrapGap}px;`}
    }
    .spt-zeus-content {
      display: flex;
      flex-direction: column;
      align-items: center;
      box-sizing: border-box;
      ${
        isPortrait
          ? `
      width: 100%;
      gap: ${zeusContentGap}px;
      flex: 1;
      min-height: 0;
      justify-content: flex-start;
      `
          : `
      gap: ${zeusDesign.contentGap}px;
      flex: none;
      transform-origin: top center;
      `
      }
    }
    .spt-zeus-sections {
      width: 100%;
      ${
        isPortrait
          ? `
      flex: 1;
      min-height: 0;
      display: flex;
      flex-direction: column;
      gap: ${zeusSectionRowGap}px;
      `
          : `
      display: grid;
      grid-template-columns: 1fr 1fr;
      row-gap: ${zeusDesign.sectionRowGap}px;
      column-gap: ${zeusDesign.sectionInnerGap}px;
      justify-content: center;
      `
      }
      align-items: center;
      justify-items: center;
    }
    .spt-zeus-section {
      display: flex;
      flex-direction: row;
      align-items: stretch;
      justify-content: center;
      gap: ${isPortrait ? zeusSectionInnerGap : zeusDesign.sectionInnerGap}px;
      ${isPortrait ? "width: 100%; box-sizing: border-box;" : "width: auto; max-width: 100%; justify-self: center;"}
    }
    .spt-zeus-section:last-child {
      ${isPortrait ? "" : "grid-column: 1 / -1; max-width: 52%; justify-self: center;"}
    }
    .spt-zeus-img-wrap {
      flex: 0 0 auto;
      width: auto;
      align-self: stretch;
      max-width: ${Math.round(getImgWidth(SYMBOL_SLIDE_2_IMG_RATIO, layoutDevice, isPortrait) * 100)}%;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .spt-zeus-img {
      ${isPortrait ? "height: 95%; max-height: 95%;" : `height: auto; max-height: ${zeusDesign.imgH}px;`}
      max-width: 95%;
      object-fit: contain;
    }
    .spt-zeus-desc {
      flex: 1;
      min-width: 0;
      text-align: center;
    }
    .spt-zeus-content .spt-feature-desc {
      white-space: normal;
    }

    /* ── Slide 3: Regular Symbols Grid ── */
    .spt-grid-content {
      flex: 1; min-height: 0;
      width: 100%;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: ${gridContentGap}px;
    }
    .spt-grid-block {
      justify-content: center;
      width: 100%;
    }
    .spt-grid-block-2col {
      display: grid;
      grid-template-columns: auto auto;
      justify-items: center;
      row-gap: ${gridPortraitRowGap}px;
      column-gap: ${gridPortraitColGap}px;
    }
    .spt-grid-block-3col {
      display: flex;
      flex-wrap: nowrap;
      gap: ${gridPortraitColGap}px;
    }
    .spt-grid-block-3col .spt-grid-cell {
      flex: 0 0 auto;
    }
    .spt-grid-section {
      width: 100%;
      display: flex;
      /* Landscape-only element — never wrap, so the 2-row layout is fixed and
         symbols only scale (via the height fit) rather than reflowing. */
      flex-wrap: nowrap;
      align-items: center;
      justify-content: center;
      gap: ${Math.round(linePad * 1.5)}px;
    }
    .spt-grid-section.spt-grid-mp {
      justify-content: center;
      align-items: ${isPortrait ? "center" : "flex-start"};
    }
    .spt-grid-section.spt-grid-lp {
      flex-direction: row;
      flex-wrap: nowrap;
      align-items: ${isPortrait ? "center" : "flex-start"};
      justify-content: center;
    }
    .spt-grid-cell {
      display: flex;
      flex-direction: column;
      align-items: center;
      text-align: center;
      gap: ${gridCellGap}px;
    }
    .spt-grid-img-wrap {
      /* height set inline via symbolSlide3Sizes.imgMaxH */
      flex-shrink: 0;
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .spt-grid-img {
      max-height: 100%;
      max-width: 100%;
      object-fit: contain;
    }
    .spt-grid-values {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 0;
      flex-shrink: 0;
    }
    .spt-grid-pv {
      /* font-size set inline via symbolSlide3Sizes.pvFontSize */
      font-weight: 600;
      color: rgba(255,255,255,0.8);
      line-height: 1.3;
      white-space: nowrap;
    }
  `;
  }, [
    chrome,
    linePad,
    slideContentGap,
    slideContentSpacedGap,
    isPortrait,
    isTunedMobileLandscape,
    isTabletPortrait,
    isStandardMobilePortrait,
    isSmallMobilePortrait,
    layoutDevice,
    isTunedShellPortrait,
    shellContentWidth,
    arrowSafeZone,
    separatorLineWidth,
    separatorHeight,
    closeButtonSize,
    closeButtonRight,
    separatorMarginTop,
    separatorMarginBottom,
    headerPaddingBottom,
    headerTitleFontSize,
    slideTitleFontSize,
    featureSubtitleFontSize,
    glowTop,
    glowWidth,
  ]);

  if (!shouldRender) return null;

  const closeIconSrc = getUITextureAsDataURL("button_x");

  return (
    <div
      ref={overlayRef}
      className="spt-overlay"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        // Visible-area size (excludes browser toolbars). 100vw/100vh on iOS
        // Chrome resolve to the taller layout viewport, which pushes the
        // footer/pagination behind the bottom toolbar. dimensions.* come from
        // visualViewport (the real visible area) and match the fit math used
        // everywhere else in the overlay.
        width: dimensions.width,
        height: dimensions.height,
        background:
          "radial-gradient(ellipse at center, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.92) 75%, rgba(0,0,0,0.97) 100%)",
        zIndex: 1000,
        display: "flex",
        flexDirection: "column",
        padding: `${shellTopPad}px 0 ${shellBottomPad}px 0`,
        boxSizing: "border-box",
        opacity: 0,
        // When the iOS fullscreen overlay is up, hide the paytable for real:
        // opacity alone leaves the subtree hit-testable (the active slide sets
        // pointer-events:auto, defeating the root's `none`), so it sits on top of
        // the canvas and swallows the fullscreen swipe. visibility:hidden can't be
        // overridden by a descendant, so the whole subtree stops hit-testing.
        visibility: isHidden ? "hidden" : "visible",
        pointerEvents: isHidden ? "none" : "auto",
      }}
    >
      {/* ── Decorative glow — standard mobile portrait only ── */}
      {isTunedStandardPortrait && (
        <img
          className="spt-glow"
          src={gameImages.glow || ""}
          alt=""
          aria-hidden="true"
        />
      )}

      {/* ── HEADER: title + close + separator ── */}
      <div className="spt-header">
        <div className="spt-header-title-row">
          <h1 className="spt-title">
            {t("paytable_title", { defaultValue: "Paytable" })}
          </h1>
          <button
            className="spt-close-btn"
            onClick={handleClose}
            style={{
              backgroundImage: closeIconSrc ? `url('${closeIconSrc}')` : "none",
            }}
          />
        </div>
        <div className="spt-separator" />
      </div>

      {/* ── CAROUSEL ── */}
      <PaytableSlider
        slides={slides}
        dimensions={dimensions}
        device={layoutDevice}
        orientation={orientation}
        chrome={chrome}
        toolbar={toolbar}
        isPortrait={isPortrait}
        shouldRender={shouldRender}
        contentWidth={
          isTabletPortrait ||
          isSmallMobilePortrait ||
          isStandardMobilePortrait ||
          isTunedMobileLandscape
            ? shellContentWidth
            : undefined
        }
        landscapeChromeSideOffset={tunedLandscapeChromeSideOffset}
        isTunedMobileLandscape={isTunedMobileLandscape}
        isWideDesktop={isWideDesktop}
        footerMarginTop={footerMarginTop}
        glowSrc={gameImages.glow}
        isHidden={isHidden}
      />

      {/* Overlay-scoped styles */}
      <style>{overlayStyles}</style>
    </div>
  );
};

export default PaytableOverlay;
